// ZoneManagement.js

import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from "@mui/material";

import {
  
  getPrinterZones,
  createPrinterZone,
  updatePrinterZone,
  deletePrinterZone,
} from "apis/printAPIs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useDispatch } from "react-redux";
import { showErrorSnackbar } from "store/actions/snackbarMessage";

const ZoneDialog = ({
  isOpen,
  onClose,
  onAddZone,
  onUpdateZone,
  floors,
  printers,
  initialZone,
}) => {
  const dispatch = useDispatch();
  const isEditMode = Boolean(initialZone);
  const [zoneType, setZoneType] = useState(initialZone?.zoneType || "");
  const [zoneName, setZoneName] = useState(initialZone?.zoneName || "");
  const [zoneDescription, setZoneDescription] = useState(initialZone?.zoneDescription || "");
  const [selectedFloor, setSelectedFloor] = useState(initialZone?.zoneFloor || null);
  const [selectedPrinter, setSelectedPrinter] = useState(initialZone?.printer?.printerName || "");
  const [selectedTables, setSelectedTables] = useState(initialZone?.zoneTables || []);
  const showErrorMessage = (msg) => dispatch(showErrorSnackbar(msg));

  useEffect(() => {
    if (isOpen) {
      setZoneType(initialZone?.zoneType || "");
      setZoneName(initialZone?.zoneName || "");
      setZoneDescription(initialZone?.zoneDescription || "");
      setSelectedFloor(initialZone?.zoneFloor || null);
      setSelectedPrinter(initialZone?.printer?.printerName || "");
      setSelectedTables(initialZone?.zoneTables || []);
    }
    // eslint-disable-next-line
  }, [isOpen, initialZone]);

  const relatedTables =
    selectedFloor && selectedFloor !== ""
      ? (floors.find((area) => area.name === selectedFloor)?.tables || []).slice().sort((a, b) => a - b)
      : [];

  const handleSave = () => {
    if (zoneName === "") {
      showErrorMessage("Please enter a zone name");
      return;
    }
    if (zoneType === "floorTable" && selectedFloor === null) {
      showErrorMessage("Please select a floor");
      return;
    }
    if (selectedPrinter === "") {
      showErrorMessage("Please select a printer");
      return;
    }
    const printer = printers.find((printer) => printer.printerName === selectedPrinter);
    const zoneData = {
      zoneType,
      zoneName,
      zoneDescription,
      zoneFloor: selectedFloor,
      zoneTables: selectedTables,
      printer,
      id: initialZone?.id || new Date().getTime(),
    };
    if (isEditMode) {
      onUpdateZone(zoneData);
    } else {
      onAddZone(zoneData);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>{isEditMode ? "Edit Zone" : "Create Zone"}</DialogTitle>
      <DialogContent>
        <Select
          value={zoneType}
          onChange={(e) => setZoneType(e.target.value)}
          fullWidth
          disabled={isEditMode}
        >
          <MenuItem value="floorTable">Floor and Table Based</MenuItem>
          <MenuItem value="logic">Logic Zone</MenuItem>
        </Select>
        <TextField
          label="Zone Name"
          value={zoneName}
          onChange={(e) => setZoneName(e.target.value)}
          fullWidth
        />
        {zoneType === "floorTable" && (
          <>
            <Select
              value={selectedFloor}
              onChange={(e) => {
                setSelectedFloor(e.target.value);
                setSelectedTables([]);
              }}
              disabled={isEditMode}
            >
              {floors?.map((floor) => (
                <MenuItem key={floor.name} value={floor.name}>
                  {floor.name}
                </MenuItem>
              ))}
            </Select>
            <Select
              multiple
              value={selectedTables}
              onChange={(e) => setSelectedTables(e.target.value)}
            >
              {relatedTables.map((tableNumber) => (
                <MenuItem key={tableNumber} value={tableNumber}>
                  {tableNumber}
                </MenuItem>
              ))}
            </Select>
          </>
        )}
        <TextField
          label="Zone Description"
          value={zoneDescription}
          onChange={(e) => setZoneDescription(e.target.value)}
          fullWidth
        />
        <Select
          value={selectedPrinter}
          onChange={(e) => setSelectedPrinter(e.target.value)}
        >
          {printers.map((printer) => (
            <MenuItem key={printer.printerName} value={printer.printerName}>
              {printer.printerName}
            </MenuItem>
          ))}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary">
          {isEditMode ? "Update Zone" : "Add Printer Zone"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ZoneManagement = ({ floors, printers }) => {
  const [isZoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [editZone, setEditZone] = useState(null);

  const createZoneMutation = useMutation({
    mutationFn: createPrinterZone
  });
  const updateZoneMutation = useMutation({
    mutationFn: updatePrinterZone
  });
  const zonesListName = "printerZones";

  const queryClient = useQueryClient();
  const { data: zones = [], isLoading } = useQuery({
    queryKey: [zonesListName],
    queryFn: getPrinterZones
  });

  const handleAddZone = (zoneData) => {
    createZoneMutation.mutate(zoneData, {
      onSuccess: () => {
        setZoneDialogOpen(false);
        queryClient.invalidateQueries(zonesListName);
      },
    });
  };

  const handleUpdateZone = (zoneData) => {
    updateZoneMutation.mutate(zoneData, {
      onSuccess: () => {
        setEditZone(null);
        queryClient.invalidateQueries(zonesListName);
      },
    });
  };

  const deleteZoneMutation = useMutation({
    mutationFn: deletePrinterZone
  });

  const handleDeleteZone = (zoneId) => {
    deleteZoneMutation.mutate(zoneId, {
      onSuccess: () => {
        setZoneDialogOpen(false);
        queryClient.invalidateQueries(zonesListName);
      },
    });
  };

  if (isLoading) return <LinearProgress />;

  return (
    <div>
      <Button onClick={() => { setZoneDialogOpen(true); setEditZone(null); }} color="primary">
        New Printer Zone
      </Button>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Floor/Tables</TableCell>
            <TableCell>Printer</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {zones.map((zone) => (
            <TableRow key={zone.id}>
              <TableCell>{zone.zoneType}</TableCell>
              <TableCell>{zone.zoneName}</TableCell>
              <TableCell>
                {zone.zoneType === "floorTable"
                  ? `${zone.zoneFloor} ${(zone.zoneTables || []).slice().sort((a, b) => a - b).join(", ")}`
                  : ""}
              </TableCell>
              <TableCell>{zone.printer && zone.printer.printerName}</TableCell>
              <TableCell>{zone.zoneDescription}</TableCell>
              <TableCell>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => { setEditZone(zone); setZoneDialogOpen(true); }}
                  style={{ marginRight: 8 }}
                >
                  Update
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleDeleteZone(zone.id)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ZoneDialog
        floors={floors}
        printers={printers}
        isOpen={isZoneDialogOpen}
        onClose={() => { setZoneDialogOpen(false); setEditZone(null); }}
        onAddZone={handleAddZone}
        onUpdateZone={handleUpdateZone}
        initialZone={editZone}
      />
    </div>
  );
};

export default ZoneManagement;
